import { useGetMeQuery } from "@/redux/feature/user/userApi";
import { useEffect, useState } from "react";
import PersonalDetails from "./components/PersonalDetails";
import ProfileCompletionStatus from "./components/ProfileCompletionStatus";
import ProfileHeader from "./components/ProfileHeader";
import ProfileProfessionalDetails from "./components/ProfileProfessionalDetails";
import ProjectSetup from "./components/ProjectSetup";
import { useAppDispatch, useAppSelector } from "@/redux/hooks/hooks";
import { TUser } from "@/types/types";
import Spinner from "@/components/ui/Spinner";
import { resetUserChanges, setUser } from "@/redux/feature/user/userSlice";
import usePolling from "@/hooks/usePolling";
import { useCreatorStatus } from "@/hooks/useCreatorStatuts";
import IncompleteApplicationReminderBanner from "../dashboard/components/IncompleteApplicationReminderBanner";
import CreatorExtrasDetails from "./components/CreatorExtrasDetails";
import WardrobeBackgroundPortfolio from "./components/WardrobeBackgroundPortfolio";
import ProfilePortfolioVideos from "./components/ProfilePortfolioVideos";

const Profile = () => {
  const { user } = useAppSelector((state) => state.auth);

  const { pollingInterval } = usePolling({
    templateIds: ["video_change_request_approved"],
  });
  const { data, isLoading } = useGetMeQuery(user?.email, {
    skip: !user?.email,
    pollingInterval,
  });
  const userData = data?.data;

  const {
    shouldShowPopup,
    status,
    isError: isStatusError,
  } = useCreatorStatus(user?.email as string);

  // const [removeUser, { isError, isLoading: loadingDeletingUser }] =
  //   useRemoveUserMutation();

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (userData) {
      dispatch(resetUserChanges(userData));
      // console.log("User Slice Data:", userSliceData);
    }
  }, [userData, dispatch]);
  const setup = data?.data as TUser;

  const hasProps = (setup?.props ?? []).some((p) => p?.trim?.().length > 0);
  const hasGreenScreen = !!setup?.greenScreen;

  const hasProjectSetupData =
    setup?.hasRelativesInVideo !== undefined ||
    hasProps ||
    hasGreenScreen ||
    (setup?.noiselessRoomAndMicrophoneSpecs?.hasAudioEquipment &&
      !!setup?.noiselessRoomAndMicrophoneSpecs?.details?.trim?.()) ||
    (setup?.cameraOrSmartphoneSpecs?.hasCamera &&
      !!setup?.cameraOrSmartphoneSpecs?.details?.trim?.()) ||
    (setup?.lightingSpecs?.hasLighting &&
      !!setup?.lightingSpecs?.details?.trim?.()) ||
    (setup?.additionalInfo?.hasAdditionalExperience &&
      !!setup?.additionalInfo?.details?.trim?.());

  if (isLoading) {
    return (
      <div>
        <ProfileHeader />
        <div className="bg-[#F9FAFB] w-full min-h-screen py-8">
          <div className="max-w-[945px] mx-auto p-4 md:p-0 space-y-16">
            <div className="space-y-2.5 mb-8">
              <h5 className="text-lg leading-5 tracking-[-1%] font-semibold">
                Personal Details
              </h5>
              <p className="text-[#475467] leading-5">
                Update your photo and personal details here.
              </p>
            </div>
            <Spinner />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <ProfileHeader />
      <div className="bg-[#F9FAFB] w-full min-h-screen py-8">
        {status?.isNotInitiated ? (
          <div className="px-4 md:px-20 ">
            <IncompleteApplicationReminderBanner />
          </div>
        ) : (
          <div className="w-full max-w-[945px] mx-auto p-4 md:p-0 space-y-16">
            {/* Personal Details */}
            <div>
              <div className="flex justify-between items-center">
                <div className="space-y-2.5 mb-8">
                  <h5 className="text-lg leading-5 tracking-[-1%] font-semibold">
                    Personal Details
                  </h5>
                  <p className="text-[#475467] leading-5">
                    Update your photo and personal details here.
                  </p>
                </div>
              </div>
              <div className="flex flex-col-reverse md:flex-row justify-center gap-8">
                <div className="w-full sm:w-[69%] mx-auto">
                  <PersonalDetails data={userData as TUser} />
                </div>
                <div className="w-full sm:w-[69%] lg:w-[31%] mx-auto">
                  <ProfileCompletionStatus data={userData as TUser} />
                </div>
              </div>
            </div>

            <ProfilePortfolioVideos data={userData as TUser} />

            <WardrobeBackgroundPortfolio data={userData as TUser} />

            {/* Professional Details */}
            <div className="w-full sm:w-[69%] mx-auto lg:mx-0">
              <div className="space-y-2.5 mb-8">
                <h5 className="text-lg leading-5 tracking-[-1%] font-semibold">
                  Professional Details
                </h5>
                <p className="text-[#475467] leading-5">
                  Update your past works and brands you have worked with
                </p>
              </div>
              <ProfileProfessionalDetails data={userData} />
            </div>

            {/* Project Setup */}
            {hasProjectSetupData && (
              <div className="w-full sm:w-[69%] mx-auto lg:mx-0">
                <div className="space-y-2.5 mb-8">
                  <h5 className="text-lg leading-5 tracking-[-1%] font-semibold">
                    Project Setup Information
                  </h5>
                  <p className="text-[#475467] leading-5">
                    Update the Attires, Settings available and Services Provided
                  </p>
                </div>
                <ProjectSetup data={data?.data as TUser} />
                {/* <div className="text-[13px] leading-5 tracking-[-1%] space-y-2 mt-5">
                <p className="font-medium">Disclaimer</p>
                <p className="text-[#667085]">
                  Please note that by uploading your videos, you consent to
                  their use in displaying to clients as part of the project.
                  Your content will be showcased to potential clients to
                  demonstrate your previous work and expertise
                </p>
              </div> */}
              </div>
            )}

            <CreatorExtrasDetails data={userData as TUser} />
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
